import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
  Chip,
  InputAdornment,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  FileCopy as CopyIcon,
  Visibility as PreviewIcon,
  Settings as SettingsIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Language as WebIcon,
  CheckCircle as CheckIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: theme.spacing(2),
    borderRadius: "12px 12px 0 0",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  button: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: "bold",
    padding: theme.spacing(1.5, 3),
  },
  primaryButton: {
    background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(45deg, #1976D2 30%, #00B0FF 90%)",
    },
  },
  successButton: {
    background: "linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(45deg, #388E3C 30%, #689F38 90%)",
    },
  },
  linkCard: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : theme.palette.grey[50],
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[300]}`,
    transition: "all 0.3s",
    "&:hover": {
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      transform: "translateY(-2px)",
    },
  },
  linkUrl: {
    fontFamily: "monospace",
    fontSize: "0.9rem",
    color: theme.palette.primary.main,
    wordBreak: "break-all",
    backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : "#fff",
    padding: theme.spacing(1),
    borderRadius: 4,
    border: `1px solid ${theme.palette.type === 'dark' ? '#444' : theme.palette.grey[300]}`,
  },
  socialIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  previewCard: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : "#f5f5f5",
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
  statusChip: {
    fontWeight: "bold",
  },
}));

const SmartLinkConfig = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    whatsappNumber: "",
    messages: {
      instagram: "Olá! Vim do Instagram",
      facebook: "Olá! Vim do Facebook",
      whatsapp: "Olá! Vim do WhatsApp",
      default: "Olá! Vim do site",
    },
    enabled: true,
    slug: "",
    companyId: user?.companyId || null,
  });
  const [links, setLinks] = useState(null);
  // Domain default — em ordem de prioridade:
  //  1. REACT_APP_BACKEND_URL (config do .env, padrão do projeto)
  //  2. window.location.origin (mesma origem do front, útil quando back+front
  //     compartilham domínio em produção)
  //  3. http://localhost:8080 (último recurso, dev)
  const defaultDomain = (() => {
    const fromEnv = process.env.REACT_APP_BACKEND_URL;
    if (fromEnv) return fromEnv.replace(/\/+$/, ""); // remove barra final
    if (typeof window !== "undefined" && !window.location.origin.includes("localhost")) {
      return window.location.origin;
    }
    return "http://localhost:8080";
  })();
  const [domain, setDomain] = useState(defaultDomain);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  const [utmData, setUtmData] = useState({
    source: "",
    medium: "",
    campaign: "",
    term: "",
    content: "",
  });

  const getUtmLink = () => {
    if (!links || !links.universal_link) return "";
    const params = new URLSearchParams();
    if (utmData.source) params.append("utm_source", utmData.source);
    if (utmData.medium) params.append("utm_medium", utmData.medium);
    if (utmData.campaign) params.append("utm_campaign", utmData.campaign);
    if (utmData.term) params.append("utm_term", utmData.term);
    if (utmData.content) params.append("utm_content", utmData.content);
    
    const qs = params.toString();
    return qs ? `${links.universal_link}?${qs}` : links.universal_link;
  };

  // Carregar configurações ao montar
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/smart-link/config");
      // O backend retorna sempre um `data.config` — mesmo quando ainda
      // não há configuração salva (success=false vem com defaults).
      // Aproveitamos o que veio para popular o form.
      if (data?.config) {
        setConfig(prev => ({
          ...prev,
          ...data.config,
          messages: { ...prev.messages, ...(data.config.messages || {}) }
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validação básica de telefone (precisa ter código país + DDD + número)
    const phoneDigits = (config.whatsappNumber || "").replace(/\D/g, "");
    if (!phoneDigits) {
      toast.error("Informe o número do WhatsApp");
      return;
    }
    if (phoneDigits.length < 12 || phoneDigits.length > 13) {
      toast.error("Número inválido. Formato: 55 + DDD + número (ex: 5575991900605)");
      return;
    }

    if (config.slug && !/^[a-z0-9-]+$/.test(config.slug)) {
      toast.error("Slug deve conter apenas letras minúsculas, números e hífen");
      return;
    }

    try {
      setLoading(true);
      const payload = { ...config, whatsappNumber: phoneDigits };
      const response = await api.post("/smart-link/reload", payload);

      if (response.data.success) {
        // Atualiza estado com a versão sanitizada que voltou do servidor
        if (response.data.config) {
          setConfig(prev => ({
            ...prev,
            ...response.data.config,
            messages: { ...prev.messages, ...(response.data.config.messages || {}) }
          }));
        }
        setSnackbar({
          open: true,
          message: "✅ Configurações salvas com sucesso!",
          severity: "success",
        });
        toast.success("Configurações salvas!");
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      const msg = error?.response?.data?.error || "Erro ao salvar configurações";
      setSnackbar({ open: true, message: `❌ ${msg}`, severity: "error" });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLinks = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/smart-link/generate-links?domain=${encodeURIComponent(domain)}`);
      if (data.success) {
        setLinks(data);
        setSnackbar({
          open: true,
          message: "🔗 Links gerados com sucesso!",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Erro ao gerar links:", error);
      toast.error("Erro ao gerar links");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    try {
      // O preview público precisa de um slug ou ID
      const identifier = config.slug || config.companyId || "";
      if (!identifier) {
        toast.warn("Salve a configuração primeiro para gerar o preview");
        return;
      }
      // Endpoint público — não precisa de auth.
      const { data } = await api.get(`/smart-link/${identifier}/preview`);
      setPreviewData(data);
      setPreviewDialog(true);
    } catch (error) {
      console.error("Erro ao carregar preview:", error);
      toast.error("Erro ao carregar preview");
    }
  };

  const handleOpenTestLink = () => {
    const id = config.slug || config.companyId;
    if (!id) {
      toast.warn("Salve a configuração primeiro");
      return;
    }
    const url = `${domain}/smart-link/${id}`;
    window.open(url, "_blank", "noopener");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("📋 Link copiado!");
  };

  const socialNetworks = [
    { key: "instagram", name: "Instagram", icon: <InstagramIcon style={{ color: "#E4405F" }} />, color: "#E4405F" },
    { key: "facebook", name: "Facebook", icon: <FacebookIcon style={{ color: "#1877F2" }} />, color: "#1877F2" },
    { key: "whatsapp", name: "WhatsApp", icon: <WhatsAppIcon style={{ color: "#25D366" }} />, color: "#25D366" },
    { key: "default", name: "Google/Outros", icon: <WebIcon style={{ color: "#4285F4" }} />, color: "#4285F4" },
  ];

  return (
    <Container maxWidth="lg" className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          <LinkIcon fontSize="large" />
          Configuração do Smart Link
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={config.enabled ? "ATIVADO" : "DESATIVADO"}
            color={config.enabled ? "primary" : "default"}
            size="small"
            className={classes.statusChip}
          />
          <Tooltip title="Recarregar">
            <IconButton onClick={loadConfig} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Preview da detecção">
            <span>
              <IconButton
                onClick={handlePreview}
                color="primary"
                disabled={!config.slug && !config.companyId}
              >
                <PreviewIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* O que é + aviso de localhost */}
      <Alert severity="info" style={{ marginBottom: 16 }}>
        <strong>O que é Smart Link?</strong> Um link único (ex: <code>{domain}/smart-link/{config.slug || config.companyId || "sua-empresa"}</code>) que você cola na bio do Instagram, Facebook, TikTok, em anúncios, etc. Quando o cliente clicar, o sistema detecta a origem (Instagram/Facebook/Google/etc) e abre o WhatsApp com uma mensagem específica de cada canal — assim você sabe DE ONDE veio cada cliente.
      </Alert>

      {domain.includes("localhost") && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          <strong>⚠️ Você está usando localhost.</strong> O link gerado só funciona na sua máquina. Para funcionar publicamente (Instagram/Facebook/etc) você precisa de um domínio público com HTTPS apontando pro backend.
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress />
        </Box>
      )}

      {/* Configuração do WhatsApp */}
      <Card className={classes.card}>
        <Box className={classes.cardHeader}>
          <WhatsAppIcon />
          <Typography variant="h6">Configuração Básica</Typography>
        </Box>
        <CardContent className={classes.cardContent}>
          <TextField
            fullWidth
            label="Slug Personalizado (URL amigável)"
            placeholder="minha-empresa"
            value={config.slug}
            onChange={(e) => {
              const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
              setConfig({ ...config, slug: value });
            }}
            className={classes.textField}
            variant="outlined"
            helperText="Use apenas letras minúsculas, números e hífen. Ex: minha-empresa, loja-abc"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Número do WhatsApp"
            placeholder="5575991900605"
            value={config.whatsappNumber}
            onChange={(e) => setConfig({ ...config, whatsappNumber: e.target.value })}
            className={classes.textField}
            variant="outlined"
            helperText="Formato: Código do país + DDD + número (sem espaços)"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <WhatsAppIcon style={{ color: "#25D366" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                color="primary"
              />
            }
            label={config.enabled ? "Smart Link Habilitado" : "Smart Link Desabilitado"}
          />
        </CardContent>
      </Card>

      {/* Mensagens Personalizadas */}
      <Card className={classes.card}>
        <Box className={classes.cardHeader}>
          <SettingsIcon />
          <Typography variant="h6">Mensagens Personalizadas</Typography>
        </Box>
        <CardContent className={classes.cardContent}>
          <Grid container spacing={3}>
            {socialNetworks.map((social) => (
              <Grid item xs={12} md={6} key={social.key}>
                <Box display="flex" alignItems="center" mb={1}>
                  {social.icon}
                  <Typography variant="subtitle1" style={{ marginLeft: 8, fontWeight: "bold" }}>
                    {social.name}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label={`Mensagem para ${social.name}`}
                  placeholder={`Olá! Vim do ${social.name}`}
                  value={config.messages[social.key]}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      messages: { ...config.messages, [social.key]: e.target.value },
                    })
                  }
                  variant="outlined"
                  multiline
                  rows={2}
                />
              </Grid>
            ))}
          </Grid>

          <Box mt={3} display="flex" gap={2}>
            <Button
              variant="contained"
              className={`${classes.button} ${classes.primaryButton}`}
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
              fullWidth
            >
              Salvar Configurações
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Gerar Links */}
      <Card className={classes.card}>
        <Box className={classes.cardHeader}>
          <LinkIcon />
          <Typography variant="h6">Gerar Links</Typography>
        </Box>
        <CardContent className={classes.cardContent}>
          <TextField
            fullWidth
            label="Seu Domínio (URL pública do backend)"
            placeholder="https://seudominio.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className={classes.textField}
            variant="outlined"
            helperText={
              process.env.REACT_APP_BACKEND_URL
                ? `✓ Detectado automaticamente do .env (REACT_APP_BACKEND_URL=${process.env.REACT_APP_BACKEND_URL}). Edite só se for testar com outro domínio.`
                : "⚠️ REACT_APP_BACKEND_URL não está no .env do frontend. Configure pra puxar automático em produção."
            }
          />
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              className={`${classes.button} ${classes.successButton}`}
              startIcon={<LinkIcon />}
              onClick={handleGenerateLinks}
              disabled={loading || !config.enabled}
              fullWidth
            >
              Gerar Todos os Links
            </Button>
            <Button
              variant="outlined"
              className={classes.button}
              startIcon={<PreviewIcon />}
              onClick={handleOpenTestLink}
              disabled={!config.enabled || (!config.slug && !config.companyId)}
            >
              Testar agora
            </Button>
          </Box>

          {links && (
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                🔗 Seu Link Universal:
              </Typography>
              <Box className={classes.linkCard}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Use este MESMO link em TODAS as redes sociais — o sistema detecta a origem automaticamente quando dá:
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography className={classes.linkUrl} style={{ flex: 1 }}>
                    {links.universal_link}
                  </Typography>
                  <Tooltip title="Copiar">
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(links.universal_link)}
                      color="primary"
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Divider style={{ margin: "24px 0" }} />

              {/* 🆕 Variações por canal — mais granular */}
              {links.variations && (
                <>
                  <Typography variant="h6" gutterBottom>
                    🎯 Variações por canal (rastreio preciso)
                  </Typography>
                  <Alert severity="info" style={{ marginBottom: 16 }}>
                    Use estes links se quiser <strong>diferenciar canais</strong> que o sistema não detecta sozinho (TikTok, biolink, anúncios específicos, e-mail, etc.). Cada um já vem com <code>utm_source</code> embutido — quando o cliente clicar, a origem chega marcada e o relatório mostra direitinho.
                  </Alert>
                  <Grid container spacing={2}>
                    {Object.entries(links.variations).map(([key, url]) => {
                      const labels = {
                        instagram_organic: "📷 Instagram (orgânico)",
                        instagram_bio: "📷 Instagram (bio)",
                        instagram_stories: "📷 Instagram (stories)",
                        instagram_ads: "📷 Instagram Ads",
                        facebook_organic: "👤 Facebook (orgânico)",
                        facebook_ads: "👤 Facebook Ads",
                        tiktok_bio: "🎵 TikTok (bio)",
                        tiktok_video: "🎵 TikTok (vídeo)",
                        youtube_description: "▶️ YouTube",
                        linkedin: "💼 LinkedIn",
                        google_ads: "🔍 Google Ads",
                        whatsapp_status: "💬 Status WhatsApp",
                        site_button: "🌐 Botão no site",
                        email_signature: "📧 Assinatura de e-mail",
                      };
                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          <Box className={classes.linkCard} style={{ marginBottom: 8 }}>
                            <Typography variant="caption" style={{ fontWeight: "bold", display: "block", marginBottom: 4 }}>
                              {labels[key] || key}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography className={classes.linkUrl} style={{ flex: 1, fontSize: "0.75rem" }}>
                                {url}
                              </Typography>
                              <Tooltip title="Copiar">
                                <IconButton size="small" onClick={() => copyToClipboard(url)} color="primary">
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}

              <Divider style={{ margin: "24px 0" }} />

              <Typography variant="h6" gutterBottom>
                📱 Links por Rede Social:
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(links.links).map(([key, url]) => {
                  const social = socialNetworks.find((s) => s.key === key);
                  return (
                    <Grid item xs={12} md={6} key={key}>
                      <Box className={classes.linkCard}>
                        <Box display="flex" alignItems="center" mb={1}>
                          {social?.icon || <WebIcon />}
                          <Typography variant="subtitle2" style={{ marginLeft: 8, fontWeight: "bold" }}>
                            {social?.name || key}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography className={classes.linkUrl} style={{ flex: 1, fontSize: "0.8rem" }}>
                            {url}
                          </Typography>
                          <Tooltip title="Copiar">
                            <IconButton size="small" onClick={() => copyToClipboard(url)} color="primary">
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {links.instructions && links.instructions[key] && (
                          <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
                            💡 {links.instructions[key]}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* UTM Builder */}
      <Card className={classes.card}>
        <Box className={classes.cardHeader}>
          <LinkIcon />
          <Typography variant="h6">Construtor de Links UTM</Typography>
        </Box>
        <CardContent className={classes.cardContent}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Crie links rastreáveis personalizados para suas campanhas. Preencha os campos abaixo e copie o link final gerado.
          </Typography>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Source (Origem)"
                placeholder="ex: instagram, google"
                value={utmData.source}
                onChange={(e) => setUtmData({ ...utmData, source: e.target.value })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Medium (Mídia)"
                placeholder="ex: cpc, banner, email"
                value={utmData.medium}
                onChange={(e) => setUtmData({ ...utmData, medium: e.target.value })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Campaign (Campanha)"
                placeholder="ex: promo_verao"
                value={utmData.campaign}
                onChange={(e) => setUtmData({ ...utmData, campaign: e.target.value })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Term (Termo da Pesquisa)"
                placeholder="ex: marketing_digital"
                value={utmData.term}
                onChange={(e) => setUtmData({ ...utmData, term: e.target.value })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Content (Conteúdo do Anúncio)"
                placeholder="ex: video_01"
                value={utmData.content}
                onChange={(e) => setUtmData({ ...utmData, content: e.target.value })}
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Box mt={3} p={2} style={{ backgroundColor: "inherit", borderRadius: 8, border: "1px solid #ccc" }}>
            <Typography variant="subtitle2" color="textSecondary">
              Link Final:
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Typography className={classes.linkUrl} style={{ flex: 1, backgroundColor: "inherit" }}>
                {getUtmLink() || "Gere seus links primeiro para criar URLs personalizadas."}
              </Typography>
              <Tooltip title="Copiar Link UTM">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(getUtmLink())}
                    color="primary"
                    disabled={!getUtmLink()}
                  >
                    <CopyIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PreviewIcon color="primary" />
            Preview da Detecção
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <Box>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon style={{ color: "#4CAF50" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Origem Detectada"
                    secondary={previewData.detected_origin}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WhatsAppIcon style={{ color: "#25D366" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Número WhatsApp"
                    secondary={previewData.whatsapp_number}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LinkIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Mensagem"
                    secondary={previewData.message}
                  />
                </ListItem>
              </List>
              <Box className={classes.previewCard}>
                <Typography variant="caption" color="textSecondary">
                  URL do WhatsApp:
                </Typography>
                <Typography variant="body2" style={{ wordBreak: "break-all", marginTop: 8 }}>
                  {previewData.whatsapp_url}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SmartLinkConfig;